import { useEffect } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createActionsHook, createStateHook } from '@atlassian/react-sweet-state';
import { useSpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/page-ready-state/index.tsx';

const store = createStore({
	name: 'priority-manager',
	initialState: { tier: 1 },
	actions: {
		setTier:
			(tier: number) =>
			({ setState, getState }) =>
				setState({
					tier: Math.max(getState().tier, tier),
				}),
	},
});

export const usePriorityActions = createActionsHook(store);
export const useCurrentPriority = createStateHook(store, { selector: (state) => state.tier });

// allow max 5 seconds between tier 2(app ready) and tier 3(work is done)
const MAX_TIME_IN_TIER_2 = 5000;

/**
 * defines loading priorities(tiers) for the application
 */
export const loadingPriority = {
	// tier 0 - "early scripts" is outside of this component reach and responsibility
	/**
	 * Tier 1 - initial loading phase, everything is critical
	 */
	critical: 1,
	/**
	 * Tier 2 - app is ready, but some work is still in progress
	 * This is the phase where we can start loading non-critical resources
	 */
	nonCritical: 2,
	/**
	 * Tier 3 - background phase, all work is done, we can load background resources
	 * This is the phase where we can start loading background resources
	 */
	background: 3,
} as const;

/**
 * @deprecated - this is POC component to manage loading priorities in SPA.
 * This component connects SPA and UFO states in a way we can get something useful today
 * Implementation will be changed in the future.
 */
export function PriorityManager() {
	const [{ isReady }] = useSpaStatePageReady();
	const { setTier } = usePriorityActions();
	useEffect(() => {
		if (isReady) {
			performance.mark('priority:non-critical-phase');
			setTier(loadingPriority.nonCritical);
			// ensure we are advancing in no later than MAX_TIME_IN_TIER_2
			const tm = setTimeout(() => setTier(loadingPriority.background), MAX_TIME_IN_TIER_2);
			return () => {
				clearTimeout(tm);
			};
		}
	}, [isReady, setTier]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const onPageReady = () => {
				performance.mark('priority:background-phase');
				setTier(loadingPriority.background);
			};
			window.addEventListener('UFO_FINISH_INTERACTION', onPageReady);
			return () => {
				window.removeEventListener('UFO_FINISH_INTERACTION', onPageReady);
			};
		}
	}, [setTier]);
	return null;
}
